import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { Dispatch, SetStateAction } from 'react';
import { ErrorToast, SuccessToast } from '../components';
import { ApiResponseType } from '../types/types';
import {
  AddUserWalletResponse,
  DeleteUserWalletResponse,
  GetUserWalletsResponse,
  UpdateUserWalletResponse,
  UserWalletModel,
  UserWalletStatus
} from '../types/wallets-types';
import {
  flipmeConnect,
  flipMeGetInfo,
  flipmeNFTWithdraw,
  redeemFlipmeCard,
  checkRedeemAvailability,
  flipMeRankRewards,
  redeemFlipmeCardInRank
} from '../views/FlipMeWallet/FlipMeWallet.actions';

export const createUserWalletAsync = async ({
  address,
  label
}: {
  address: string;
  label: string;
}): Promise<ApiResponseType<AddUserWalletResponse>> => {
  const { data } = await axios.post<ApiResponseType<AddUserWalletResponse>>(
    `/user/wallet`,
    { address, label }
  );

  return data;
};

export const deleteUserWalletAsync = async (
  id: number
): Promise<ApiResponseType<DeleteUserWalletResponse>> => {
  const { data } = await axios.delete<
    ApiResponseType<DeleteUserWalletResponse>
  >(`/user/wallet/${id}`);
  return data;
};

export const editUserWalletAsync = async (id: number, label: string) => {
  const { data } = await axios.put<ApiResponseType<UpdateUserWalletResponse>>(
    `/user/wallet`,
    { id, label }
  );
  return data;
};

export const reactivateUserWalletAsync = async (
  id: number
): Promise<ApiResponseType<any>> => {
  const { data } = await axios.put<ApiResponseType<any>>(`/user/wallet/${id}`);
  return data;
};

export const getWallets = createAsyncThunk('getWallets', async () => {
  const { data } = await axios.get<ApiResponseType<GetUserWalletsResponse>>(
    `/user/wallet`
  );
  return data.data?.userWallets.sort(
    (a: UserWalletModel, b: UserWalletModel) => {
      return b.id - a.id;
    }
  );
});

export const reactivateUserWallet = createAsyncThunk(
  'reactivateUserWallet',
  async ({ id }: { id: number }) => {
    await reactivateUserWalletAsync(id);
    SuccessToast('Successfully reactivated wallet');
    return id;
  }
);

export const deleteUserWallet = createAsyncThunk(
  'deleteUserWallet',
  async ({ id }: { id: number }) => {
    await deleteUserWalletAsync(id);
    SuccessToast('Successfully deactivated wallet');
    return id;
  }
);

export const editUserWallet = createAsyncThunk(
  'editUserWallet',
  async ({
    id,
    label,
    setIsOpen4
  }: {
    id: number;
    label: string;
    setIsOpen4: Dispatch<SetStateAction<boolean>>;
  }) => {
    const { data } = await editUserWalletAsync(id, label);
    setIsOpen4(false);
    SuccessToast('Successfully edited wallet');
    return data?.userWallet;
  }
);

interface CreateUserWalletProps {
  address: string;
  setAddress: Dispatch<SetStateAction<string>>;
  setStep?: Dispatch<SetStateAction<number>>;
  label: string;
  setLabel: Dispatch<SetStateAction<string>>;
}

export const createUserWallet = createAsyncThunk(
  'createUserWallet',
  async ({
    address,
    setAddress,
    setStep,
    label,
    setLabel
  }: CreateUserWalletProps) => {
    try {
      const { data } = await createUserWalletAsync({ label, address });

      setAddress('');
      setLabel('');

      if (setStep) {
        setStep(4);
      }

      return data?.userWallet;
    } catch (error: any) {
      ErrorToast(error.response.data.error);
    }
  }
);

export interface walletsSliceType {
  loading: boolean;
  wallets: UserWalletModel[];
  flipMeStatus: number;
  flipmeWallet: any;
  isPersonalFlipmeRedeemRequired: boolean;
  loadingRedeemFlipmeCards: boolean;
  isRankRedeemAvailable: any;
  flipmeCard: any;
}

const initialState = {
  loading: false,
  wallets: [],
  flipMeStatus: 0,
  flipmeWallet: {},
  isPersonalFlipmeRedeemRequired: false,
  loadingRedeemFlipmeCards: false,
  isRankRedeemAvailable: '',
  flipmeCard: null
} as walletsSliceType;

const walletsSlice = createSlice({
  name: 'wallets',
  initialState,
  reducers: {},

  extraReducers: (builder) => {
    builder.addCase(getWallets.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getWallets.fulfilled, (state, { payload }) => {
      state.loading = false;
      if (payload) {
        state.wallets = payload;
      }
    });
    builder.addCase(getWallets.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(createUserWallet.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(createUserWallet.fulfilled, (state, { payload }) => {
      state.loading = false;
      if (payload) {
        state.wallets = [payload, ...state.wallets];
      }
    });
    builder.addCase(createUserWallet.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(reactivateUserWallet.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(reactivateUserWallet.fulfilled, (state, { payload }) => {
      state.loading = false;
      const index = state.wallets.findIndex((p) => p.id === payload);
      state.wallets[index].status = UserWalletStatus.ACTIVATED;
    });
    builder.addCase(reactivateUserWallet.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(deleteUserWallet.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(deleteUserWallet.fulfilled, (state, { payload }) => {
      state.loading = false;
      const index = state.wallets.findIndex((p) => p.id === payload);
      state.wallets[index].status = UserWalletStatus.DEACTIVATED;
    });
    builder.addCase(deleteUserWallet.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(editUserWallet.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(editUserWallet.fulfilled, (state, { payload }) => {
      state.loading = false;
      if (payload) {
        const index = state.wallets.findIndex((p) => p.id === payload.id);
        state.wallets[index] = payload;
      }
    });
    builder.addCase(editUserWallet.rejected, (state) => {
      state.loading = false;
    });

    //FLIPME WALLET CONNECT
    builder.addCase(flipMeGetInfo.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(flipMeGetInfo.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.flipMeStatus = payload.status;
      state.flipmeWallet = payload.walletAddress;
      state.isPersonalFlipmeRedeemRequired =
        payload.isPersonalFlipmeRedeemRequired;
    });
    builder.addCase(flipMeGetInfo.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(flipmeNFTWithdraw.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(flipmeNFTWithdraw.fulfilled, (state, { payload }) => {
      state.loading = false;
    });
    builder.addCase(flipmeNFTWithdraw.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(flipmeConnect.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(flipmeConnect.fulfilled, (state, { payload }) => {
      state.loading = false;
    });
    builder.addCase(flipmeConnect.rejected, (state) => {
      state.loading = false;
    });

    //REDEEM FLIPME CARDS
    builder.addCase(redeemFlipmeCard.pending, (state) => {
      state.loadingRedeemFlipmeCards = true;
    });
    builder.addCase(redeemFlipmeCard.fulfilled, (state, { payload }) => {
      state.loadingRedeemFlipmeCards = false;
    });
    builder.addCase(redeemFlipmeCard.rejected, (state) => {
      state.loadingRedeemFlipmeCards = false;
    });

    builder.addCase(checkRedeemAvailability.fulfilled, (state, { payload }) => {
      state.isRankRedeemAvailable = payload;
    });

    builder.addCase(flipMeRankRewards.fulfilled, (state, { payload }) => {
      state.flipmeCard = payload.flipmeCard;
    });

    //REDEEM FLIPME CARD
    builder.addCase(redeemFlipmeCardInRank.pending, (state) => {
      state.loadingRedeemFlipmeCards = true;
    });

    builder.addCase(redeemFlipmeCardInRank.fulfilled, (state, { payload }) => {
      state.loadingRedeemFlipmeCards = false;
    });
    builder.addCase(redeemFlipmeCardInRank.rejected, (state) => {
      state.loadingRedeemFlipmeCards = false;
    });
  }
});

export default walletsSlice.reducer;
